import moment, { Moment } from 'moment';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import { DateType, ProjectId } from 'api/models';
import { Project, ProjectAPI } from 'api/project';
import { DatePicker } from 'components/atoms/date-picker';
import { Form } from 'components/atoms/form';
import { HumanisedDate } from 'components/atoms/humanised-date';
import { toastService } from 'components/molecules/toast';
import { useTranslation } from 'hooks/use-translation';
import { useProjectsQuery } from 'queries';
import { getProjectDetailQueryKey } from 'queries/common';
import { ALL_PROJECTS_QUERY_KEY } from 'queries/constants';
import { defaultFormat } from 'utils/date-field/formatter';
import { queueMacrotask } from 'utils/macrotask';
import {
  ContentWrapper,
  Modal,
  StaticContentWrapper,
  StyledCheckbox,
  StyledForm,
  Title,
  WarnText,
} from './components';

interface Props {
  projectId?: number;
  onProjectShift: (
    project: Project,
    shiftAllocations: boolean,
    dates: { oldStartDate: DateType; oldDueDate: DateType },
  ) => void;
  onCancel: () => void;
}

export const ShiftProjectStartDate: FC<Props> = ({
  projectId,
  onProjectShift,
  onCancel,
}) => {
  const queryClient = useQueryClient();
  const { data: projects } = useProjectsQuery();
  const choosenProject = useMemo(
    () => projects?.find((p) => p.projectId === projectId),
    [projectId, projects],
  );
  const [shiftTo, setShiftTo] = useState<Moment | null>();
  const [shiftAllocation, setShiftAllocation] = useState(false);
  const [actionPending, setActionPending] = useState(false);
  const errorMessage = {
    title: useTranslation('failure'),
    message: useTranslation(
      'resource_management.shift_start_date.error.message',
    ),
  };
  const reset = useCallback(() => {
    setShiftTo(null);
    setShiftAllocation(false);
  }, []);

  const onShift = useCallback(() => {
    if (projectId == null || shiftTo == null || choosenProject == null) {
      return;
    }
    const oldStartDate = choosenProject.startDate;
    const oldDueDate = choosenProject.dueDate;

    setActionPending(true);
    ProjectAPI.updateProject(projectId, {
      startDate: defaultFormat(shiftTo),
      shiftAllocations: shiftAllocation,
    })
      .then(async (response) => {
        await queryClient.setQueryData(
          getProjectDetailQueryKey(ProjectId(projectId)),
          response,
        );
        await queryClient.invalidateQueries(ALL_PROJECTS_QUERY_KEY);
        queueMacrotask(() => {
          onProjectShift(response, shiftAllocation, {
            oldStartDate,
            oldDueDate,
          });
        });
        reset();
      })
      .catch(() => {
        toastService({
          title: errorMessage.title,
          message: errorMessage.message,
        }).error();
      })
      .finally(() => {
        setActionPending(false);
      });
  }, [
    choosenProject,
    errorMessage.message,
    errorMessage.title,
    onProjectShift,
    projectId,
    queryClient,
    reset,
    shiftAllocation,
    shiftTo,
  ]);

  return (
    <Modal
      visible={choosenProject != null}
      title={
        choosenProject != null ? (
          <Title projectName={choosenProject.projectName} />
        ) : null
      }
      width={384}
      onCancel={() => {
        onCancel();
        reset();
      }}
      okButtonProps={{
        disabled: shiftTo == null,
        loading: actionPending,
      }}
      okText={<FormattedMessage id="shift" />}
      onOk={onShift}
    >
      {choosenProject != null ? (
        <ContentWrapper>
          <StaticContentWrapper>
            <FormattedMessage id="resource_management.shift_start_date.current_start_date" />
            :
            <strong>
              <HumanisedDate timestamp={moment(choosenProject.startDate)} />
            </strong>
          </StaticContentWrapper>
          <StyledForm layout="vertical">
            <Form.Item
              label={
                <FormattedMessage id="resource_management.shift_start_date.shift_to" />
              }
            >
              <DatePicker
                value={shiftTo}
                onChange={(value) => setShiftTo(value)}
                allowClear={false}
                disabledDate={(date) =>
                  moment(choosenProject.startDate).isSame(date, 'day')
                }
                defaultPickerValue={moment(choosenProject.startDate)}
              />
            </Form.Item>
          </StyledForm>
          <WarnText>
            <FormattedMessage id="resource_management.shift_start_date.info_text" />
          </WarnText>
          <StyledCheckbox
            disabled={shiftTo == null}
            checked={shiftAllocation}
            onChange={(e) => setShiftAllocation(e.target.checked)}
          >
            <FormattedMessage
              id="resource_management.shift_start_date.shift_allocation"
              values={{
                numberOfDays:
                  shiftTo == null
                    ? 0
                    : moment(shiftTo).diff(
                        moment(choosenProject.startDate),
                        'days',
                        false,
                      ),
                strong: (chunks) => <strong>{chunks}</strong>,
              }}
            />
          </StyledCheckbox>
        </ContentWrapper>
      ) : null}
    </Modal>
  );
};
